/**
 * Auth context: holds the current user + mode and provides login/logout.
 * Bootstrapped from /auth/me on first render.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth, portfolio, type MeResponse } from './api';

interface AuthCtx {
  me: MeResponse | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await auth.me();
      setMe(r);
      setError(null);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const r = await auth.login(email, password);
      setMe(r);
      portfolio.sync().catch(() => {});
    } catch (e: unknown) {
      const detail =
        (e as { detail?: { detail?: string } | string })?.detail ?? 'login failed';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      throw e;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      setMe(null);
    }
  }, []);

  const value = useMemo(
    () => ({ me, loading, error, signIn, signOut, refresh }),
    [me, loading, error, signIn, signOut, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
